import { useAuthStore } from '@/stores/auth.store'
import { PERMISSIONS, ROLES, USER_TYPES } from '@/constants/permissions'

/**
 * Diagnostic des permissions de menu
 */
export function diagnoseMenuPermissions() {
  const authStore = useAuthStore()
  const user = authStore.user

  if (!user) {
    console.log('❌ Aucun utilisateur connecté')
    return
  }

  console.log('🔍 Diagnostic des Permissions de Menu')
  console.log('=====================================')
  console.log(`👤 Utilisateur: ${user.nom} ${user.prenom}`)
  console.log(`🎭 Rôle: ${user.role}`)
  console.log(`👥 Type: ${user.userType}`)
  console.log(`🔐 Permissions: ${user.permissions?.length || 0}`)

  // Définition des menus avec leurs permissions
  const menuItems = [
    {
      title: 'Tableau de bord',
      to: '/dashboard',
      permissions: [],
      roles: [],
      userTypes: []
    },
    {
      title: 'Tableaux de bord',
      to: '/dashboards',
      permissions: [PERMISSIONS.DASHBOARD.READ, PERMISSIONS.DASHBOARD.CREATE],
      roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
      userTypes: []
    },
    {
      title: 'Employés',
      to: '/employes',
      permissions: [PERMISSIONS.EMPLOYEE.READ],
      roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
      userTypes: []
    },
    {
      title: 'Paie',
      to: '/paie',
      permissions: [PERMISSIONS.PAYROLL.READ],
      roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER],
      userTypes: []
    },
    {
      title: 'Absences',
      to: '/absences',
      permissions: [PERMISSIONS.ABSENCE.READ],
      roles: [],
      userTypes: [USER_TYPES.INTERNAL]
    },
    {
      title: 'Présence',
      to: '/presence',
      permissions: [PERMISSIONS.PRESENCE.READ],
      roles: [],
      userTypes: []
    },
    {
      title: 'Rémunération',
      to: '/remuneration',
      permissions: [PERMISSIONS.PAYROLL.READ, PERMISSIONS.PAYROLL.MANAGE_SALARIES],
      roles: [],
      userTypes: []
    },
    {
      title: 'Notes de frais',
      to: '/notes-frais',
      permissions: [PERMISSIONS.EXPENSE.READ],
      roles: [],
      userTypes: [USER_TYPES.INTERNAL]
    },
    {
      title: 'Documents',
      to: '/documents',
      permissions: [PERMISSIONS.FILE.UPLOAD, PERMISSIONS.FILE.DOWNLOAD],
      roles: [],
      userTypes: [USER_TYPES.INTERNAL]
    },
    {
      title: 'Équipements',
      to: '/equipements',
      permissions: [PERMISSIONS.EQUIPMENT.READ, PERMISSIONS.EQUIPMENT.CREATE],
      roles: [],
      userTypes: []
    },
    {
      title: 'Syndicats',
      to: '/syndicats',
      permissions: [PERMISSIONS.UNION.READ, PERMISSIONS.UNION.UPDATE],
      roles: [],
      userTypes: []
    },
    {
      title: 'Rapports',
      to: '/reports',
      permissions: [PERMISSIONS.REPORTS.READ, PERMISSIONS.REPORTS.EXPORT],
      roles: [],
      userTypes: []
    },
    {
      title: 'Configuration',
      to: '/configuration',
      permissions: [PERMISSIONS.SETTINGS.READ, PERMISSIONS.SETTINGS.UPDATE],
      roles: [],
      userTypes: []
    },
    {
      title: 'Utilisateurs',
      to: '/utilisateurs',
      permissions: [PERMISSIONS.USER.READ, PERMISSIONS.USER.CREATE, PERMISSIONS.USER.UPDATE],
      roles: [],
      userTypes: []
    }
  ]

  console.log('\n📋 Analyse des Menus:')
  console.log('====================')

  menuItems.forEach(item => {
    console.log(`\n🔸 ${item.title} (${item.to})`)
    
    // Vérifier les rôles
    if (item.roles.length > 0) {
      const hasRole = item.roles.includes(user.role as any)
      console.log(`   Rôles requis: ${item.roles.join(', ')}`)
      console.log(`   Votre rôle: ${user.role}`)
      console.log(`   ✅ Rôle: ${hasRole ? 'AUTORISÉ' : 'REFUSÉ'}`)
    }

    // Vérifier les types d'utilisateur
    if (item.userTypes.length > 0) {
      const hasUserType = item.userTypes.includes(user.userType as any)
      console.log(`   Types requis: ${item.userTypes.join(', ')}`)
      console.log(`   Votre type: ${user.userType}`)
      console.log(`   ✅ Type: ${hasUserType ? 'AUTORISÉ' : 'REFUSÉ'}`)
    }

    // Vérifier les permissions
    if (item.permissions.length > 0) {
      console.log(`   Permissions requises: ${item.permissions.join(', ')}`)
      const hasPermissions = item.permissions.some(permission => 
        authStore.hasPermission(permission)
      )
      console.log(`   ✅ Permissions: ${hasPermissions ? 'AUTORISÉ' : 'REFUSÉ'}`)
      
      // Détail des permissions
      item.permissions.forEach(permission => {
        const hasPermission = authStore.hasPermission(permission)
        console.log(`      ${hasPermission ? '✅' : '❌'} ${permission}`)
      })
    }

    // Résultat final
    const isVisible = checkMenuItemVisibility(item, user, authStore)
    console.log(`   🎯 RÉSULTAT: ${isVisible ? '✅ VISIBLE' : '❌ MASQUÉ'}`)
  })

  return {
    user,
    menuItems,
    visibleCount: menuItems.filter(item => checkMenuItemVisibility(item, user, authStore)).length,
    totalCount: menuItems.length
  }
}

/**
 * Vérifier si un élément de menu est visible
 */
function checkMenuItemVisibility(item: any, user: any, authStore: any): boolean {
  // Si aucun contrôle d'accès n'est défini, afficher par défaut
  if (!item.roles && !item.permissions && !item.userTypes) {
    return true
  }

  // Vérifier les rôles
  if (item.roles && item.roles.length > 0) {
    if (!item.roles.includes(user.role)) {
      return false
    }
  }

  // Vérifier les permissions
  if (item.permissions && item.permissions.length > 0) {
    const hasRequiredPermission = item.permissions.some((permission: string) => 
      authStore.hasPermission(permission)
    )
    if (!hasRequiredPermission) {
      return false
    }
  }

  // Vérifier les types d'utilisateur
  if (item.userTypes && item.userTypes.length > 0) {
    if (!item.userTypes.includes(user.userType)) {
      return false
    }
  }

  return true
}

// Exporter pour utilisation dans la console du navigateur
if (typeof window !== 'undefined') {
  (window as any).diagnoseMenuPermissions = diagnoseMenuPermissions
}
